import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useFieldArray, useFormContext, type FieldArrayWithId } from 'react-hook-form';

import { itemInitialState } from '../constants';
import { ItemOrderSchemaType, OrderSchemaType } from '../order.schema';

export type NoSavedItemsType = 'no-items' | 'not-saved';

export interface SteelItemsState {
  fields: FieldArrayWithId<OrderSchemaType, 'items', 'id'>[];
  removeItem: (index: number) => void;
  updateItem: (index: number, value: ItemOrderSchemaType) => void;
  addNewItem: () => void;
  collapsedItems: Set<number>;
  toggleItem: (index: number, forceOpen?: boolean) => void;
  hasValidationError: boolean;
  setHasValidationError: (value: boolean) => void;
  noSavedItemsType: NoSavedItemsType;
  setNoSavedItemsType: (value: NoSavedItemsType) => void;
}

const SteelItemsContext = createContext<SteelItemsState | null>(null);

export const SteelItemsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { control, getValues, setValue } = useFormContext<OrderSchemaType>();
  const { fields, append, remove } = useFieldArray({ control, name: 'items' });

  const [collapsedItems, setCollapsedItems] = useState<Set<number>>(new Set([0]));
  const [hasValidationError, setHasValidationError] = useState(false);
  const [noSavedItemsType, setNoSavedItemsType] = useState<NoSavedItemsType>('no-items');

  /** o pedido carregado chega de uma vez: todos os itens dele nascem colapsados, como antes */
  const hydrated = useRef(false);
  useEffect(() => {
    if (hydrated.current || fields.length <= 1) return;
    hydrated.current = true;
    setCollapsedItems(new Set(fields.map((_, index) => index)));
  }, [fields]);

  const toggleItem = useCallback((index: number, forceOpen?: boolean) => {
    setCollapsedItems(prev => {
      const next = new Set(prev);
      if (forceOpen || next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const updateItem = useCallback((index: number, value: ItemOrderSchemaType) => {
    if (value.isSaved && hasValidationError && noSavedItemsType === 'not-saved') {
      setHasValidationError(false);
    }
    const currentItems = getValues('items') || [];
    const newItems = [...currentItems];
    newItems[index] = value;
    setValue('items', newItems, { shouldDirty: true, shouldValidate: false });
  }, [hasValidationError, noSavedItemsType, getValues, setValue]);

  const addNewItem = useCallback(() => {
    if (noSavedItemsType === 'no-items') setHasValidationError(false);
    append(itemInitialState);
  }, [noSavedItemsType, append]);

  const value: SteelItemsState = {
    fields, removeItem: remove, updateItem, addNewItem,
    collapsedItems, toggleItem,
    hasValidationError, setHasValidationError,
    noSavedItemsType, setNoSavedItemsType,
  };

  return <SteelItemsContext.Provider value={value}>{children}</SteelItemsContext.Provider>;
};

export const useSteelItems = (): SteelItemsState => {
  const ctx = useContext(SteelItemsContext);
  if (!ctx) throw new Error('useSteelItems precisa de um SteelItemsProvider acima na árvore');
  return ctx;
};
